import { Button, Icon, Text, VStack } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from '../../assets/icons'
import { button } from '../../themes/styles'
import FormModal from './FormModal'

export interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  /** Main confirmation prompt (centered). */
  message?: React.ReactNode
  /** Secondary hint shown under the message in a muted style. */
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  isLoading?: boolean
  isConfirmDisabled?: boolean
  /** Use the destructive (red) confirm button. Defaults to true (delete-style). */
  isDestructive?: boolean
  /** Override the header icon. Pass null to hide it. Defaults to an alert circle. */
  icon?: React.ReactNode | null
  /** Extra content rendered below the message (e.g. a small form). */
  children?: React.ReactNode
}

/**
 * Unified confirmation / discard dialog: a title, a centered message, and
 * Cancel / Confirm buttons. Built on FormModal so it shares the app's modal
 * shell. Use this for every "are you sure?" / delete / discard prompt instead
 * of hand-rolling a Modal or AlertDialog.
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  description,
  confirmLabel,
  cancelLabel,
  isLoading = false,
  isConfirmDisabled = false,
  isDestructive = true,
  icon,
  children,
}) => {
  const { t } = useTranslation()
  const showIcon = icon !== null
  const confirmVariant = isDestructive ? 'delete' : 'mono'

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={onConfirm}
      size="md"
      footer={
        <>
          <Button {...button.secondary} onClick={onClose} isDisabled={isLoading}>
            {cancelLabel ?? t('common.actions.cancel')}
          </Button>
          <Button
            variant={confirmVariant}
            flex={1}
            onClick={onConfirm}
            isLoading={isLoading}
            isDisabled={isConfirmDisabled}
          >
            {confirmLabel ?? t('common.actions.confirm')}
          </Button>
        </>
      }
    >
      <VStack spacing={3} textAlign="center" w="100%">
        {showIcon &&
          (icon ?? (
            <Icon color={isDestructive ? 'error.100' : 'solid.900'} w="48px" h="48px" viewBox="0 0 24 24" fill="none">
              <AlertCircleIcon />
            </Icon>
          ))}
        {message && (
          <Text textStyle="l-s" color="solid.900">
            {message}
          </Text>
        )}
        {description && (
          <Text textStyle="l-xs" color="solid.400">
            {description}
          </Text>
        )}
        {children}
      </VStack>
    </FormModal>
  )
}

export default ConfirmModal
